import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './WOE.css';
import { API_URL, buildUrl  } from '../../config/api';
import { getCurrentUser } from '../../utils/auth';
import { isIsraelUser } from '../../utils/locationUtils';
import { AppointmentService } from '../../services/appointmentService';
import { formatShortDate } from '../../utils/dateUtils';
import CreateAppointmentModal  from '../Calendar/CreateAppointmentModal';

const WOEWigRooms = ({
  customerInfo,
  wigDetails,
  orderItems = [],
  accessories = [],
  barcode,
  serialCode,
  orderNo,
  wigRoomsData = [],
  draftPickupNote = null,
  draftRooms = null,
  draftStylistName = null,
  draftQcNotes = null,
  onPrevious,
  onNext,
  onStateChange
}) => {
  // State for tracking task data
  const [taskData, setTaskData] = useState({
    selectedLocation: 'Pomona',
    locations: {
      Pomona: {
        selectedRoom: '',
        selectedTask: '',
        selectedWorker: '',
        dueDate: '',
        taskNotes: '',
        qcNotes: '', 
        pendingTasks: 0,
        inProcessTasks: 0,
        showCalendar: false
      },
      Lakewood: {
        selectedRoom: '',
        selectedTask: '',
        selectedWorker: '',
        dueDate: '',
        taskNotes: '',
        qcNotes: '', 
        pendingTasks: 0,
        inProcessTasks: 0,
        showCalendar: false
      },
      Israel: {
        selectedRoom: '',
        selectedTask: '',
        selectedWorker: '',
        dueDate: '',
        taskNotes: '',
        qcNotes: '', 
        pendingTasks: 0,
        inProcessTasks: 0,
        showCalendar: false
      },
      China: {
        selectedRoom: '',
        selectedTask: '',
        selectedWorker: '',
        dueDate: '',
        taskNotes: '',
        qcNotes: '', 
        pendingTasks: 0,
        inProcessTasks: 0,
        showCalendar: false
      }
    },
    showRoutingSettings: true,
    showRouting: false,
    allTasks: Array.isArray(wigRoomsData) ? wigRoomsData.map(task => ({
      ...task,
      id: task.id || Date.now() + Math.floor(Math.random() * 1000) // Ensure all tasks have an id
    })) : [] 
  });

    // console.log('Customer Info:', customerInfo);
    const [showModal, setShowModal] = useState(false);
    const [customerAppointments, setCustomerAppointments] = useState([]);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
    const [qcWorkers, setQcWorkers] = useState([]);
    const [showAllLocations, setShowAllLocations] = useState(false);
    const [selectedQcWorker, setSelectedQcWorker] = useState('');
    const [showWholesaleDatePicker, setShowWholesaleDatePicker] = useState(false);

    // Warranty for this wig (resolved by barcode). When the wig is under
    // warranty we prompt the receptionist to add a Wash & Set room.
    const [warrantyCheck, setWarrantyCheck] = useState(null);
    const [washSetPromptAnswered, setWashSetPromptAnswered] = useState(false);
  //  initialize roomsData
  const [roomsData, setRoomsData] = useState({
    Pomona: { rooms: [], tasks: {} },
    Lakewood: { rooms: [], tasks: {} },
    Israel: { rooms: [], tasks: {} },
    China: { rooms: [], tasks: {} }
  });


  const currentUser = getCurrentUser();


  // 🔧 REVERTED: Only save state on navigation (Next/Previous), not continuously
  // This prevents interference with date pickers, modals, and form inputs



const fetchCustomerAppointments = useCallback(async (customerNo) => {
  try {
    const appointments = await AppointmentService.fetchFilteredAppointments(customerNo);
    setCustomerAppointments(appointments);
    return appointments;
  } catch (error) {
    console.error('Failed to fetch appointments:', error);
    setCustomerAppointments([]);
    return [];
  }
}, []);

const fetchRoomsForLocation = async (location) => {
  try {
    const response = await axios.get(`${API_URL}/api/get-rooms/${location}`);
    return response.data
      .filter(room => !room.RoomDescription.toLowerCase().includes('quality'))
      .map(room => ({
        roomNo: room.RoomNo,
        roomName: `${room.RoomNo} ${room.RoomDescription}`,
        parentRoomNo: room.ParentRoomNo
      }));
  } catch (error) {
    console.error(`Error fetching ${location} rooms:`, error);
    return [];
  }
};

const fetchAllLocationsRooms = useCallback(async () => {
  const locations = ['Pomona', 'Lakewood', 'Israel', 'China'];
  const roomUpdates = {};
  for (const location of locations) {
    roomUpdates[location] = await fetchRoomsForLocation(location);
  }
  setRoomsData(prev => {
    const updated = { ...prev };
    for (const location of locations) {
      updated[location] = { ...prev[location], rooms: roomUpdates[location] };
    }
    return updated;
  });
}, []);

useEffect(() => {
  fetchAllLocationsRooms();
}, []);

// Pre-fill Pomona QC Notes field when a draft provides qcNotes (even empty string = stylist confirmed none)
useEffect(() => {
  if (draftQcNotes != null) {
    setTaskData(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        Pomona: { ...prev.locations.Pomona, qcNotes: draftQcNotes }
      }
    }));
  }
}, [draftQcNotes]);

const handleRoomChange = async (location, roomNo) => {
  // Update the task data state
  setTaskData(prev => ({
    ...prev,
    locations: {
      ...prev.locations,
      [location]: {
        ...prev.locations[location],
        selectedRoom: roomNo,
        selectedTask: '',
        selectedWorker: ''
      }
    }
  }));
  
  if (roomNo) {
    try {
      // Fetch tasks for the selected room
      const tasksResponse = await axios.get(`${API_URL}/api/get-room-tasks/${roomNo}`);
      
      // Fetch workers for the selected room
      const workersResponse = await axios.get(`${API_URL}/api/get-room-workers/${roomNo}`);
      
      // console.log('Task data structure:', tasksResponse.data);
      // console.log('Worker data structure:', workersResponse.data);

      // Update the roomsData state with the fetched tasks and workers
      setRoomsData(prev => ({
        ...prev,
        [location]: {
          ...prev[location],
          tasks: {
            ...prev[location].tasks,
            [roomNo]: tasksResponse.data
          },
          workers: {
            ...prev[location].workers,
            [roomNo]: workersResponse.data
          }
        }
      }));
    } catch (error) {
      console.error('Error fetching room data:', error);
    }
  }
};


const handleQcNotesChange = (location, notes) => {
  setTaskData(prev => ({
    ...prev,
    locations: {
      ...prev.locations,
      [location]: {
        ...prev.locations[location],
        qcNotes: notes
      }
    }
  }));
};

 

  // Handle task selection
  const handleTaskChange = (location, task) => {
    setTaskData(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        [location]: {
          ...prev.locations[location],
          selectedTask: task
        }
      }
    }));
  };

  // Handle worker selection
  const handleWorkerChange = (location, worker) => {
    setTaskData(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        [location]: {
          ...prev.locations[location],
          selectedWorker: worker
        }
      }
    }));
  };

  // Handle date selection
  const handleDateChange = (location, date) => {
    setTaskData(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        [location]: {
          ...prev.locations[location],
          dueDate: date,
          showCalendar: false
        }
      }
    }));
  };

  
  // const toggleCalendar = (location) => {
  //   // Always show the calendar for the selected location
  //   // and hide it for all other locations
  //   const updatedLocations = {};
  //   Object.keys(taskData.locations).forEach(loc => {
  //     updatedLocations[loc] = {
  //       ...taskData.locations[loc],
  //       showCalendar: loc === location
  //     };
  //   });
    
  //   setTaskData(prev => ({
  //     ...prev,
  //     locations: updatedLocations
  //   }));
  // };

  

  // Handle task notes change
  const handleTaskNotesChange = (location, notes) => {
    setTaskData(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        [location]: {
          ...prev.locations[location],
          taskNotes: notes
        }
      }
    }));
  };

  
// Add a new task (store locally for now)
const addTask = (location) => {
  const locationData = taskData.locations[location];
  
  if (!locationData.selectedRoom || !locationData.selectedTask ) {
    alert('Please select room and task before adding.');
    return;
  }

  const newTask = {
    id: Date.now() + Math.floor(Math.random() * 1000), // More unique ID
    roomNo: locationData.selectedRoom,
    roomTask: locationData.selectedTask,
    assignedWorker: locationData.selectedWorker,
    taskDueDate: locationData.dueDate || '',
    taskNotes: locationData.taskNotes,
    qcNotes: locationData.qcNotes, // ✅ ADD THIS
    location
  };

  setTaskData(prev => ({
    ...prev,
    allTasks: [...prev.allTasks, newTask],
    locations: {
      ...prev.locations,
      [location]: {
        ...prev.locations[location],
        taskNotes: '',
        dueDate: ''
      }
    }
  }));
};

  // Delete task
  const deleteTask = (taskId) => {
    setTaskData(prev => ({
      ...prev,
      allTasks: prev.allTasks.filter(task => task.id !== taskId)
    }));
  };

  // Update a single field on a task directly (inline editing)
  const updateTaskField = (taskId, field, value) => {
    setTaskData(prev => ({
      ...prev,
      allTasks: prev.allTasks.map(t => t.id === taskId ? { ...t, [field]: value } : t)
    }));
  };

  // Auto-add default rooms on mount for new orders
  const autoAddDefaultRooms = () => {
    // Only add defaults when this is a new order (no existing wigRoomsData)
    if (Array.isArray(wigRoomsData) && wigRoomsData.length > 0) return;
    if (customerInfo?.orderType === 'Wash & Set') return;
    if (isIsraelUser()) return;

    const defaultTasks = [
      ...(currentUser === 'Ashley' ? [{ roomNo: '17001', roomTask: 'In Transit to Pomona' }] : []),
      { roomNo: '6001', roomTask: "Rachel's Room" },
      { roomNo: '1001', roomTask: 'Build Wefts' },
      { roomNo: '1002', roomTask: 'Machine' },
      { roomNo: '3001', roomTask: 'HandSew' },
      { roomNo: '2002', roomTask: 'Color' },
      { roomNo: '2003', roomTask: 'Part & Bleach the Knots' },
      { roomNo: '1003', roomTask: 'Comb, Clips, Band...' },
      { roomNo: '4001', roomTask: 'Treatment' },
      { roomNo: '5001', roomTask: 'Wash & Set' }
    ];

    const newTasks = defaultTasks.map((t, i) => ({
      id: Date.now() + i * 10,
      roomNo: t.roomNo,
      roomTask: t.roomTask,
      assignedWorker: '',
      taskDueDate: '',
      taskNotes: '',
      qcNotes: '',
      location: 'Pomona',
      status: 'Pending'
    }));

    setTaskData(prev => ({
      ...prev,
      allTasks: [...newTasks, ...prev.allTasks]
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      autoAddDefaultRooms();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Add this useEffect to fetch tasks and workers for existing tasks
  useEffect(() => {
    const fetchTasksForExistingRooms = async () => {
      if (!wigRoomsData || wigRoomsData.length === 0) return;
      
      const newRoomsData = { ...roomsData };
      
      // Create a set of unique room numbers
      const uniqueRooms = new Set();
      wigRoomsData.forEach(task => {
        if (task.roomNo && task.location) {
          uniqueRooms.add(`${task.location}-${task.roomNo}`);
        }
      });
      
      // Fetch tasks and workers for each unique room
      for (const roomKey of uniqueRooms) {
        const [location, roomNo] = roomKey.split('-');
        
        try {
          // Fetch tasks for the selected room
          const tasksResponse = await axios.get(`${API_URL}/api/get-room-tasks/${roomNo}`);
          
          // Fetch workers for the selected room
          const workersResponse = await axios.get(`${API_URL}/api/get-room-workers/${roomNo}`);
          
          newRoomsData[location] = {
            ...newRoomsData[location],
            tasks: {
              ...newRoomsData[location].tasks,
              [roomNo]: tasksResponse.data
            },
            workers: {
              ...newRoomsData[location].workers,
              [roomNo]: workersResponse.data
            }
          };
        } catch (error) {
          console.error(`Error fetching data for ${location} room ${roomNo}:`, error);
        }
      }
      
      setRoomsData(newRoomsData);
    };

    fetchTasksForExistingRooms();
  }, []);

  /// Updated toggleRoutingSettings function with API refresh
    const toggleRoutingSettings = async () => {
      // First toggle the UI state
      setTaskData(prev => ({
        ...prev,
        showRoutingSettings: !prev.showRoutingSettings,
        showRouting: !prev.showRouting
      }));

      // Refresh room data whenever routing is opened
      if (!taskData.showRouting) {
        try {
          await fetchAllLocationsRooms();
          await fetchQcWorkers();
        } catch (error) {
          console.error('❌ Error refreshing room data:', error);
          alert('Error refreshing room data. Please try again.');
        }
      }
    };

  

const formatDate = (dateString) => {
  if (!dateString) return '';
  
  // Handle YYYY-MM-DD format (date only)
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-');
    const formatted = `${month}/${day}/${year}`;
    return formatted;
  }
  
  // Handle datetime formats like "2025-11-11T13:15" or "2025-11-11T13:15:00"
  if (typeof dateString === 'string' && dateString.includes('T')) {
    try {
      const dateStr = dateString.includes(':00') ? dateString : dateString + ':00';
      const date = new Date(dateStr);

      if (!isNaN(date.getTime())) {
        return formatShortDate(date);
      }
    } catch (error) {
      console.error('Date formatting error:', error);
    }
  }

  // NEW: Handle SQL Server datetime format like "2025-09-25 18:00:00"
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
    try {
      // Replace space with T to make it ISO format
      const isoString = dateString.replace(' ', 'T');
      const date = new Date(isoString);

      if (!isNaN(date.getTime())) {
        const formatted = formatShortDate(date);
        console.log('SQL datetime conversion:', dateString, '→', formatted);
        return formatted;
      }
    } catch (error) {
      console.error('SQL datetime formatting error:', error);
    }
  }

  // Fallback for other date formats
  try {
    const date = new Date(dateString + 'T12:00:00');
    const formatted = formatShortDate(date);
    console.log('Fallback date conversion:', dateString, '→', formatted);
    return formatted;
  } catch (error) {
    console.error('Date formatting error:', error);
    return dateString;
  }
};



const validateQualityControlRoom = () => {
  // Updated QC room numbers
  const qcRoomNumbers = ['10001', '22001', '33001']; // Pomona, Lakewood, Israel QC rooms
  
  // Find any QC rooms in the current tasks
  const qcRooms = taskData.allTasks.filter(task => 
    qcRoomNumbers.includes(task.roomNo) || 
    task.roomTask?.toLowerCase().includes('quality') ||
    task.roomTask?.toLowerCase().includes('qc') ||
    task.roomTask?.toLowerCase().includes('control')
  );
  
  
  // If order is Wash & Set, QC is not required
  if (customerInfo?.orderType === 'Wash & Set') {
    return {
      isValid: true,
      message: "QC room not required for Wash & Set orders"
    };
  }
  // Check if there's at least one QC room
  if (qcRooms.length === 0) {
    return {
      isValid: false,
      message: "Quality Control room is required. Please select a QC worker to auto-add QC room."
    };
  }
  
  // Check if all QC rooms have workers assigned
  const qcRoomsWithoutWorkers = qcRooms.filter(room => 
    !room.assignedWorker || room.assignedWorker.trim() === ''
  );
  
  if (qcRoomsWithoutWorkers.length > 0) {
    return {
      isValid: false,
      message: "All Quality Control rooms must have workers assigned. Please assign workers to all QC tasks."
    };
  }
  
  // Note: No date requirement for QC rooms
  
  return {
    isValid: true,
    message: "Quality Control validation passed"
  };
};



// 2. Add this function to get QC room number based on user location
const getQcRoomForLocation = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userLocation = userData.UserLocation || 
                         userData.location || 
                         userData.warehouse || 
                         'Pomona';
    
    const normalizedLocation = userLocation.toString().toLowerCase().trim();
    
    if (normalizedLocation === 'israel' || normalizedLocation.includes('israel')) {
      return { roomNo: '33001', location: 'Israel' };
    } else if (normalizedLocation === 'lakewood' || normalizedLocation.includes('lakewood')) {
      return { roomNo: '22001', location: 'Lakewood' };
    } else {
      return { roomNo: '10001', location: 'Pomona' };
    }
  } catch (error) {
    console.error('Error determining QC room location:', error);
    return { roomNo: '10001', location: 'Pomona' };
  }
};

// 3. Add function to fetch QC workers for current location
const fetchQcWorkers = async () => {
  try {
    const qcRoomInfo = getQcRoomForLocation();
    console.log('🔍 Fetching QC workers for room:', qcRoomInfo.roomNo);
    
    const response = await axios.get(`${API_URL}/api/get-room-workers/${qcRoomInfo.roomNo}`);
    setQcWorkers(response.data || []);
    // console.log('✅ QC workers loaded:', response.data);
  } catch (error) {
    console.error('❌ Error fetching QC workers:', error);
    setQcWorkers([]); // Fallback to empty array
  }
};

// 4. Add useEffect to fetch QC workers when component mounts
useEffect(() => {
  fetchQcWorkers();
}, []);

// 5. Add function to handle QC worker selection and auto-add QC room
const handleQcWorkerChange = (selectedWorker) => {
  console.log('🔧 QC Worker selected:', selectedWorker);
  setSelectedQcWorker(selectedWorker);
  
  if (!selectedWorker) {
    // Remove QC room if no worker selected
    setTaskData(prev => ({
      ...prev,
      allTasks: prev.allTasks.filter(task => {
        const qcRoomNumbers = ['10001', '22001', '33001'];
        return !qcRoomNumbers.includes(task.roomNo);
      })
    }));
    return;
  }
  
  const qcRoomInfo = getQcRoomForLocation();
  const currentUser = getCurrentUser();
  console.log('Current user info for QC assignment:', currentUser);
  // Remove any existing QC rooms
  const filteredTasks = taskData.allTasks.filter(task => {
    const qcRoomNumbers = ['10001', '22001', '33001'];
    return !qcRoomNumbers.includes(task.roomNo);
  });
  
  // Add the new QC room task
  const newQcTask = {
    id: Date.now() + Math.floor(Math.random() * 1000) + 1000, // Ensure unique ID
    roomNo: qcRoomInfo.roomNo,
    roomTask: 'Quality Control',
    assignedWorker: selectedWorker,
    taskDueDate: '', // Leave blank as requested
    taskNotes: '',
    qcNotes: '',
    location: qcRoomInfo.location,
    status: 'Pending'
  };
  
  // Update taskData with the new QC room
  setTaskData(prev => ({
    ...prev,
    allTasks: [...filteredTasks, newQcTask]
  }));
  
  console.log(`✅ Auto-added QC room ${qcRoomInfo.roomNo} for ${qcRoomInfo.location} with worker: ${selectedWorker}`);
};

const validateWashAndSetRequirements = () => {
  // Only validate wash room if order type is "Wash & Set"
  if (customerInfo?.orderType !== 'Wash & Set') {
    return { isValid: true, message: 'Not a Wash & Set order' };
  }
  
  const washRoomNumbers = ['5001', '23001', '34001'];
  const styleRoomNumbers = ['5001', '23001', '34001'];
  
  const washRoom = taskData.allTasks.find(task => 
    washRoomNumbers.includes(task.roomNo)
  );
  
  const styleRoom = taskData.allTasks.find(task => 
    styleRoomNumbers.includes(task.roomNo)
  );
  
  if (!washRoom) {
    return {
      isValid: false,
      message: 'Wash & Set orders require a wash room. The wash room should be auto-added.'
    };
  }
  
  
  

  
  return {
    isValid: true,
    message: 'Wash & Set validation passed'
  };
};

const handlePreviousClick = () => {
  const dataToSave = {
    wigRoomsData: taskData.allTasks,
    wigDetailsData: {
      wigData: wigDetails,
      selectedItems: orderItems,
      selectedAccessories: accessories,
      barcode: barcode,
      serialCode: serialCode
    }
  };
  onPrevious(dataToSave);
};


const handleNextClick = () => {
  // First check if customer pickup date is set
  const pickupRoom = taskData.allTasks.find(task => {
    const roomNum = parseInt(task.roomNo);
    return roomNum === 7001 || roomNum === 21001 || roomNum === 32001;
  });
  
  if (!pickupRoom?.taskDueDate) {
    alert('⚠️ Customer pickup date is required before proceeding to payment.');
    return;
  }
  
  // Check for Quality Control room
  const qcValidation = validateQualityControlRoom();
  if (!qcValidation.isValid) {
    alert(`⚠️ ${qcValidation.message}`);
    return;
  }
  
  // Check for Wash & Set requirements
  const washValidation = validateWashAndSetRequirements();
  if (!washValidation.isValid) {
    alert(`⚠️ ${washValidation.message}`);
    return;
  }
  
  // If all validations pass, proceed to next slide
  const dataToPass = {
    wigRoomsData: taskData.allTasks,
    wigDetails: wigDetails,
    selectedItems: orderItems,
    selectedAccessories: accessories,
    barcode: barcode,
    serialCode: serialCode,
    customerInfo: customerInfo,
    appointmentId: selectedAppointmentId // Pass the appointment ID
  };

  console.log("✅ All validations passed. WOEWigRooms passing to next slide:", dataToPass);
  onNext(dataToPass);
};
// Helper function to determine pickup room number based on current user location
const getPickupRoomForLocation = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    
    // ✅ FIX: Use UserLocation field directly since that's where the data is
    const userLocation = userData.UserLocation || 
                         userData.location || 
                         userData.warehouse || 
                         userData.Location || 
                         userData.Warehouse || 
                         userData.workLocation ||
                         userData.branch ||
                         userData.office ||
                         'Pomona'; // Default fallback
    
    console.log('🔍 User location detection:', {
      userLocationField: userData.UserLocation,
      detectedLocation: userLocation,
      rawUserData: userData
    });
    
    // ✅ FIX: Normalize location names and use exact matching
    const normalizedLocation = userLocation.toString().toLowerCase().trim();
    
    console.log('🎯 Normalized location:', normalizedLocation);
    
    if (normalizedLocation === 'israel' || normalizedLocation.includes('israel')) {
      console.log('✅ Matched Israel location');
      return { roomNo: '32001', location: 'Israel' };
    } else if (normalizedLocation === 'lakewood' || normalizedLocation.includes('lakewood') || normalizedLocation.includes('nj') || normalizedLocation.includes('new jersey')) {
      console.log('✅ Matched Lakewood location');
      return { roomNo: '21001', location: 'Lakewood' };
    } else if (normalizedLocation === 'pomona' || normalizedLocation.includes('pomona') || normalizedLocation.includes('ny') || normalizedLocation.includes('new york')) {
      console.log('✅ Matched Pomona location');
      return { roomNo: '7001', location: 'Pomona' };
    } else {
      console.warn('⚠️ Unknown location, defaulting to Pomona:', userLocation);
      return { roomNo: '7001', location: 'Pomona' }; // Default fallback
    }
  } catch (error) {
    console.error('Error determining user location:', error);
    return { roomNo: '7001', location: 'Pomona' }; // Safe fallback
  }
};

// 1. Add function to get wash room number based on location
const getWashRoomForLocation = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userLocation = userData.UserLocation || 
                         userData.location || 
                         userData.warehouse || 
                         'Pomona';
    
    const normalizedLocation = userLocation.toString().toLowerCase().trim();
    
    if (normalizedLocation === 'israel' || normalizedLocation.includes('israel')) {
      return { 
        washRoom: { roomNo: '34001', location: 'Israel' },
        styleRoom: { roomNo: '34001', location: 'Israel' } // Add style room
      };
    } else if (normalizedLocation === 'lakewood' || normalizedLocation.includes('lakewood')) {
      return { 
        washRoom: { roomNo: '23001', location: 'Lakewood' },
        styleRoom: { roomNo: '23001', location: 'Lakewood' } // Add style room
      };
    } else {
      return { 
        washRoom: { roomNo: '5001', location: 'Pomona' },
        styleRoom: { roomNo: '5001', location: 'Pomona' } // Add style room
      };
    }
  } catch (error) {
    console.error('Error determining wash room location:', error);
    return { 
      washRoom: { roomNo: '5001', location: 'Pomona' },
      styleRoom: { roomNo: '5002', location: 'Pomona' }
    };
  }
};

// 2. Add function to auto-add wash room if order type is "Wash & Set"
const autoAddWashRoomIfNeeded = () => {
  console.log('🔍 Checking if wash room should be auto-added for order type:', customerInfo?.orderType);
  
  // Only add wash room if order type is "Wash & Set"
  if (customerInfo?.orderType !== 'Wash & Set') {
    console.log('❌ Order type is not "Wash & Set", skipping wash room auto-add');
    return;
  }
  
  const washRoomInfo = getWashRoomForLocation();
  const washRoomNumbers = ['5001', '23001', '34001','5001', '23001', '34001'];
  
  // Check if wash room already exists
  const washRoomExists = taskData.allTasks.some(task => 
    washRoomNumbers.includes(task.roomNo)
  );
  
  if (washRoomExists) {
    console.log('✅ Wash room already exists, skipping auto-add');
    return;
  }
  
  const currentUser = getCurrentUser();
  
  // Create wash room task
  const newWashTask = {
    id: Date.now() + Math.floor(Math.random() * 1000) + 2000, // Unique ID
    roomNo: washRoomInfo.washRoom.roomNo,
    roomTask: 'Wash and Style Wig',
    assignedWorker: currentUser,
    taskDueDate: '', // Leave blank initially
    taskNotes: '',
    qcNotes: '',
    location: washRoomInfo.location,
    status: 'Pending'
  };

  // Create style room task
  // const newStyleTask = {
  //   id: Date.now() + Math.floor(Math.random() * 1000) + 3000,
  //   roomNo: washRoomInfo.styleRoom.roomNo,
  //   roomTask: 'Style Wig',
  //   assignedWorker: currentUser,
  //   taskDueDate: '',
  //   taskNotes: '',
  //   qcNotes: '',
  //   location: washRoomInfo.styleRoom.location,
  //   status: 'Pending'
  // };
  
  // Add wash room to task list
  setTaskData(prev => ({
    ...prev,
    allTasks: [...prev.allTasks, newWashTask]
  }));
  
};

// 3. Add useEffect to auto-add wash room when component mounts (if needed)
useEffect(() => {
  // Small delay to ensure all other data is loaded first
  const timer = setTimeout(() => {
    autoAddWashRoomIfNeeded();
  }, 500);

  return () => clearTimeout(timer);
}, [customerInfo?.orderType]); // Re-run if order type changes

// ── Warranty check: prompt for a Wash & Set room when the wig is covered ──
const WASH_ROOM_NUMBERS = ['5001', '5002', '23001', '34001'];

const washRoomAlreadyAdded = taskData.allTasks.some(t =>
  WASH_ROOM_NUMBERS.includes(String(t.roomNo))
);

useEffect(() => {
  const bc = (barcode || '').trim();
  if (!bc) {
    setWarrantyCheck(null);
    return;
  }

  let cancelled = false;
  setWarrantyCheck({ loading: true });

  axios.get(`${API_URL}/api/warranty/by-barcode/${encodeURIComponent(bc)}`)
    .then(res => { if (!cancelled) setWarrantyCheck({ loading: false, ...res.data }); })
    .catch(err => {
      console.error('Warranty lookup failed:', err);
      if (!cancelled) setWarrantyCheck({ loading: false, error: true });
    });

  return () => { cancelled = true; };
}, [barcode]);

const addWashSetRoom = () => {
  setWashSetPromptAnswered(true);
  if (washRoomAlreadyAdded) return;

  const washRoomInfo = getWashRoomForLocation();
  const newWashTask = {
    id: Date.now() + Math.floor(Math.random() * 1000) + 4000,
    roomNo: washRoomInfo.washRoom.roomNo,
    roomTask: 'Wash and Style Wig',
    assignedWorker: currentUser,
    taskDueDate: '',
    taskNotes: 'Added for warranty repair',
    qcNotes: '',
    location: washRoomInfo.washRoom.location,
    status: 'Pending'
  };

  setTaskData(prev => ({
    ...prev,
    allTasks: [...prev.allTasks, newWashTask]
  }));
};




const getUserLocation = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userLocation = userData.UserLocation || 
                         userData.location || 
                         userData.warehouse || 
                         userData.Location || 
                         userData.Warehouse || 
                         userData.workLocation ||
                         userData.branch ||
                         userData.office ||
                         'Pomona'; // Default fallback
    
    console.log('🔍 User location detection for rendering:', {
      userLocationField: userData.UserLocation,
      detectedLocation: userLocation,
      rawUserData: userData
    });
    
    const normalizedLocation = userLocation.toString().toLowerCase().trim();
    
    if (normalizedLocation === 'israel' || normalizedLocation.includes('israel')) {
      return 'Israel';
    } else if (normalizedLocation === 'lakewood' || normalizedLocation.includes('lakewood') || normalizedLocation.includes('nj') || normalizedLocation.includes('new jersey')) {
      return 'Lakewood';
    } else if (normalizedLocation === 'china' || normalizedLocation.includes('china')) {
      return 'China';
    } else {
      return 'Pomona'; // Default for Pomona, NY, or unknown locations
    }
  } catch (error) {
    console.error('Error determining user location:', error);
    return 'Pomona'; // Safe fallback
  }
};

// Add this function to get the other locations (excluding user's primary location)
const getOtherLocations = (userLocation) => {
  const allLocations = ['Pomona', 'Lakewood', 'Israel', 'China'];
  return allLocations.filter(location => location !== userLocation);
};

// Add this helper function at the top of your component or in a utils file
const parseEventDate = (dateString) => {
  if (!dateString) return null;
  
  // Add :00 seconds if missing
  const dateStr = dateString.includes(':00') ? dateString : dateString + ':00';
  const date = new Date(dateStr);
  
  return isNaN(date.getTime()) ? null : date;
};


// Function to auto-add or update pickup room when date is selected
const handlePickupDateChange = (selectedDate, meetingWithPerson = null) => {
  console.log('📅 Selected pickup date (raw):', selectedDate);
  console.log('👤 Meeting with person:', meetingWithPerson);

  // For simple date formats (YYYY-MM-DD), we don't need to parse
  if (!selectedDate) {
    console.error('No date provided');
    return;
  }

  const pickupRoomInfo = getPickupRoomForLocation();
  const currentUser = getCurrentUser();

  // Use MeetingWith person if available, otherwise fall back to current user
  const assignedWorker = meetingWithPerson || currentUser;
  console.log('✅ Assigning pickup to:', assignedWorker);

  // Remove any existing pickup room tasks (from any location)
  const filteredTasks = taskData.allTasks.filter(task => {
    const roomNum = parseInt(task.roomNo);
    return !(roomNum === 7001 || roomNum === 21001 || roomNum === 32001);
  });

  // Add the new pickup room task for the current location
  const newPickupTask = {
    id: Date.now() + Math.floor(Math.random() * 1000),
    roomNo: pickupRoomInfo.roomNo,
    roomTask: 'Customer Pickup',
    assignedWorker: assignedWorker,
    taskDueDate: selectedDate, // Use the date string directly
    taskNotes: '',
    location: pickupRoomInfo.location,
    status: 'Pending'
  };

  console.log('📋 Creating pickup task:', newPickupTask);

  // Update taskData with the new pickup room
  setTaskData(prev => ({
    ...prev,
    allTasks: [...filteredTasks, newPickupTask]
  }));

  console.log(`✅ Auto-added pickup room ${pickupRoomInfo.roomNo} for ${pickupRoomInfo.location} with date: ${selectedDate}, assigned to: ${assignedWorker}`);
};
  // Render location section
  const renderLocationSection = (location) => {
    const locationData = taskData.locations[location] || {};
    const rooms = roomsData[location]?.rooms || [];
    
    // Add additional null checks for tasks and workers
    const tasks = locationData.selectedRoom && roomsData[location]?.tasks ? 
      roomsData[location].tasks[locationData.selectedRoom] || [] : [];
    
    const workers = locationData.selectedRoom && roomsData[location]?.workers ? 
      roomsData[location].workers[locationData.selectedRoom] || [] : [];

    return (
      <div className="location-section">
        <div className="section-header">
          <strong style={{ fontSize: '20px' }}>{location} Rooms</strong>
        </div>
        
        <div className="room-selector">
          <select 
            value={locationData.selectedRoom}
            onChange={(e) => handleRoomChange(location, e.target.value)}
            className="form-control"
          >
            <option value="">Select Room</option>
            {rooms.map(room => (
              <option key={room.roomNo} value={room.roomNo}>
                {room.roomName}
              </option>
            ))}
          </select>
        </div>
        
        <div className="room-task-grid">
          <div>
            <label>Select Task:</label>
            <select 
              value={locationData.selectedTask}
              onChange={(e) => handleTaskChange(location, e.target.value)}
              className="form-control"
              disabled={!locationData.selectedRoom}
            >
              <option value="">Select Task</option>
              {tasks.map(task => (
                <option key={task.roomTask} value={task.roomTask}>
                  {task.RoomTask}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label>Select Worker:</label>
            <select 
              value={locationData.selectedWorker}
              onChange={(e) => handleWorkerChange(location, e.target.value)}
              className="form-control"
              disabled={!locationData.selectedRoom}
            >
              {workers.map((worker, index) => (
                <option key={index} value={worker.userName}>
                  {worker.UserName}
                </option>
              ))}
            </select>
            
            <div className="tasks-status">
              <span>Pending: <strong>{locationData.pendingTasks}</strong></span>
              <span>InProcess: <strong>{locationData.inProcessTasks}</strong></span>
            </div>
          </div>
          
          <div className="date-selector">
            <EnhancedDatePicker
              id={`dueDate-${location}`}
              label="Due Date:"
              value={locationData.dueDate || ''}
              onChange={(e) => handleDateChange(location, e.target.value)}
              placeholder="Click to select due date"
            />
          </div>
          
          <div className="task-notes">
            <label>Instructions:</label>
            <input 
              type="text" 
              value={locationData.taskNotes}
              onChange={(e) => handleTaskNotesChange(location, e.target.value)}
              className="form-control"
            />
          </div>
          
          <div className="qc-notes">
            <label>QC Notes:</label>
            <input 
              type="text" 
              value={locationData.qcNotes || ''}
              onChange={(e) => handleQcNotesChange(location, e.target.value)}
              className="form-control"
          
            />
          </div>

          <div>
            <button 
              onClick={() => addTask(location)}
              className="add-task-btn"
              disabled={!locationData.selectedRoom || !locationData.selectedTask }
            >
              Add Task
            </button>
          </div>
        </div>
      </div>
    );
  };


const EnhancedDatePicker = ({ 
  id, 
  value, 
  onChange, 
  className = 'form-control', 
  label,
  required = false,
  placeholder = "Click to select date"
}) => {
  const handleContainerClick = () => {
    const dateInput = document.getElementById(id);
    if (dateInput && dateInput.showPicker) {
      dateInput.showPicker();
    } else {
      dateInput.focus();
      dateInput.click();
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {label && (
        <label 
          htmlFor={id}
          style={{
            color: 'var(--dark-elevated)',
            fontSize: '20px',
            fontWeight: '500',
            marginBottom: '6px',
            display: 'block',
            transition: 'color 0.3s ease'
          }}
        >
          {label}
        </label>
      )}
      
      <div 
        className="enhanced-date-container"
        style={{
          position: 'relative',
          display: 'inline-block',
          width: '100%',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onClick={handleContainerClick}
        title="Click anywhere to select date"
        onMouseEnter={(e) => {
          const input = e.currentTarget.querySelector('input');
          const icon = e.currentTarget.querySelector('.calendar-icon');
          if (input) {
            input.style.borderColor = 'var(--purple-accent)';
            input.style.boxShadow = `0 0 0 2px rgba(64, 39, 176, 0.2)`;
          }
          if (icon) {
            icon.style.color = 'var(--purple-accent)';
          }
        }}
        onMouseLeave={(e) => {
          const input = e.currentTarget.querySelector('input');
          const icon = e.currentTarget.querySelector('.calendar-icon');
          if (input) {
            input.style.borderColor = 'var(--dark-border)';
            input.style.boxShadow = 'none';
          }
          if (icon) {
            icon.style.color = 'var(--text-secondary)';
          }
        }}
      >
        <input 
          id={id}
          type="date" 
          value={value || ''}
          onChange={onChange}
          className={className}
          required={required}
          style={{
            width: '100%',
            padding: '12px 40px 12px 14px',
            fontSize: '20px',
            cursor: 'pointer',
            backgroundColor: 'var(--dark-elevated)',
            color: 'var(--text-primary)',
            border: '1px solid var(--dark-border)',
            borderRadius: '6px',
            transition: 'all 0.3s ease',
            outline: 'none',
            fontFamily: 'inherit'
          }}
          
        />
        
        {/* Calendar icon with theme styling */}
        <div 
          className="calendar-icon"
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '18px',
            pointerEvents: 'none',
            color: 'var(--text-secondary)',
            zIndex: 2,
            transition: 'color 0.3s ease'
          }}
        >
          📅
        </div>
        
        {/* Invisible overlay for clicking */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent',
            cursor: 'pointer',
            zIndex: 1
          }}
        />
      </div>
    </div>
  );
};

  return (
    <div className="woe-container slide-in">
      <h2>Wig Room Assignment</h2>

      {/* ── Draft order summary banner ─────────────────────────────── */}
      {(draftStylistName || draftPickupNote || draftQcNotes != null) && (
        <div style={{
          backgroundColor: '#fffbeb',
          border: '1px solid #f59e0b',
          borderLeft: '4px solid #f59e0b',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#78350f',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>🗣 Stylist's Draft Notes</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px' }}>
            {draftStylistName && (
              <span><strong>Stylist:</strong> {draftStylistName}</span>
            )}
            {draftPickupNote && (
              <span><strong>Requested pickup:</strong> {draftPickupNote}</span>
            )}
            {draftQcNotes != null && (
              <span><strong>QC notes:</strong> {draftQcNotes || '(none noted)'}</span>
            )}
          </div>
        </div>
      )}

      {/* Summary of important info */}
      <div className="customer-summary">
        <h3>Order Information</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Order #:</span>
            <span className="summary-value">{orderNo}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Barcode:</span>
            <span className="summary-value">{barcode || 'N/A'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Serial Code:</span>
            <span className="summary-value">{serialCode || 'N/A'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Customer:</span>
            <span className="summary-label">{customerInfo?.customerName}</span>
           {/* <span className="summary-label">ID:{customerInfo.customerNo}</span> */}
          </div>
      
        </div>
      </div>
<div className="customer-pickup-due-date-section">
  <div className="due-date-input-wrapper">
    
    {/* Show wash room auto-add notification if applicable */}
    {customerInfo?.orderType === 'Wash & Set' && (
      <div style={{
        backgroundColor: 'var(--gradient-secondary)',
        color: 'var(--text-primary)',
        padding: '10px 15px',
        borderRadius: '6px',
        marginBottom: '15px',
        border: '1px solid var(--green-accent)',
        fontSize: '14px'
      }}>
        <strong>🧼 Wash & Set Order Detected:</strong> Wash and Style rooms have been automatically added to your routing.
        {(() => {
          // Only add the first matching wash room
          const washRoomNumbers = ['5001', '23001', '34001'];
          const washRoom = taskData.allTasks.find(task => washRoomNumbers.includes(task.roomNo));

          if (washRoom) {
            return ` Wash: Room ${washRoom.roomNo} - ${washRoom.location || ''}`;
          }

          return ''; // No wash room found
        })()}
      </div>
    )}

    {/* Warranty reminder: offer to add a Wash & Set room */}
    {warrantyCheck?.underWarranty === true && (
      <div style={{
        backgroundColor: '#cfe2ff',
        color: '#084298',
        padding: '12px 15px',
        borderRadius: '6px',
        marginBottom: '15px',
        border: '1px solid #9ec5fe',
        fontSize: '14px'
      }}>
        <strong>🛡 This wig is under warranty{warrantyCheck.expiresDate ? ` (expires ${warrantyCheck.expiresDate})` : ''}.</strong>
        {washRoomAlreadyAdded ? (
          <span> A Wash &amp; Set room is already in the routing.</span>
        ) : washSetPromptAnswered ? (
          <span> Noted, no Wash &amp; Set room added.</span>
        ) : (
          <>
            <span> Does it need a Wash &amp; Set room?</span>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={addWashSetRoom}
                style={{
                  padding: '5px 14px', border: 'none', borderRadius: 4,
                  background: 'var(--green-accent)', color: '#fff', fontWeight: 600, cursor: 'pointer'
                }}
              >
                Yes, add it
              </button>
              <button
                type="button"
                onClick={() => setWashSetPromptAnswered(true)}
                style={{
                  padding: '5px 14px', border: '1px solid #9ec5fe', borderRadius: 4,
                  background: 'transparent', color: '#084298', cursor: 'pointer'
                }}
              >
                No
              </button>
            </div>
          </>
        )}
      </div>
    )}


    {/* AI draft pickup date reminder */}
    {draftPickupNote && (
      <div style={{
        backgroundColor: '#fffbeb',
        border: '1px solid #f59e0b',
        borderLeft: '4px solid #f59e0b',
        borderRadius: '6px',
        padding: '10px 14px',
        marginBottom: '12px',
        fontSize: '13px',
        color: '#78350f',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
      }}>
        <span style={{ fontSize: '16px', flexShrink: 0 }}>🗣</span>
        <div>
          <strong>Stylist requested pickup:</strong> "{draftPickupNote}"
          {draftStylistName && (
            <span style={{ marginLeft: 6, background: '#fce7f3', color: '#9d174d', borderRadius: 20, padding: '1px 8px', fontSize: '12px', fontWeight: 600 }}>
              ✂️ {draftStylistName}
            </span>
          )}
          <div style={{ marginTop: 3, color: '#92400e', fontSize: '12px' }}>
            Please book an appointment and select the date below.
          </div>
        </div>
      </div>
    )}

    {/* AI draft QC notes reminder */}
    {draftQcNotes != null && (
      <div style={{
        backgroundColor: '#fffbeb',
        border: '1px solid #f59e0b',
        borderLeft: '4px solid #f59e0b',
        borderRadius: '6px',
        padding: '10px 14px',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        fontSize: '13px',
        color: '#78350f',
      }}>
        <span style={{ fontSize: '16px', flexShrink: 0 }}>🔍</span>
        <div>
          {draftQcNotes
            ? <><strong>Stylist's QC note:</strong> "{draftQcNotes}"</>
            : <><strong>QC:</strong> Stylist confirmed no special notes.</>
          }
          <div style={{ marginTop: 3, color: '#92400e', fontSize: '12px' }}>
            {draftQcNotes ? 'This has been pre-filled in the QC Notes field below.' : 'QC Notes field left blank.'}
          </div>
        </div>
      </div>
    )}

    {/* Row container for date and QC worker */}
    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', marginBottom: '10px' }}>

      {/* Customer Pickup Date */}
      <div style={{ flex: 1 }}>
        <label htmlFor="customerPickupDate">Select Customer Pickup Date:</label>
<select
  id="customerPickupDate"
  value={(() => {
    // If wholesale picker is shown, show WHOLESALE as selected
    if (showWholesaleDatePicker) {
      return 'WHOLESALE';
    }
    
    // Otherwise, show the existing pickup date logic
    const pickupRoom = taskData.allTasks.find(task => {
      const roomNum = parseInt(task.roomNo);
      return roomNum === 7001 || roomNum === 21001 || roomNum === 32001;
    });
    return pickupRoom?.taskDueDate || '';
  })()}
  onFocus={async () => {
    if (!customerInfo?.customerNo) {
      console.warn('Cannot refresh appointments: customer number is undefined');
      return;
    }
    
    try {
      await fetchCustomerAppointments(customerInfo.customerNo); 
    } catch (error) {
      console.error('Failed to refresh appointments:', error);
    }
  }}
  onChange={(e) => {
    if (e.target.value === 'CREATE_NEW') {
      setShowModal(true);
    } else if (e.target.value === 'WHOLESALE') {
      // NEW: Handle wholesale option
      setShowWholesaleDatePicker(true);
      setSelectedAppointmentId(null); // Clear appointment ID for wholesale
    } else if (e.target.value) {
      // Find the selected appointment to get its ID
      const selectedAppointment = customerAppointments.find(
        apt => apt.EventStart === e.target.value
      );

      if (selectedAppointment) {
        console.log('🔍 Full appointment object:', selectedAppointment);
        const appointmentId = selectedAppointment.ID || selectedAppointment.id || selectedAppointment.AppointmentID;
        const meetingWithPerson = selectedAppointment.MeetingWith;
        setSelectedAppointmentId(appointmentId);

        // Pass the MeetingWith person to handlePickupDateChange
        handlePickupDateChange(e.target.value, meetingWithPerson);
      } else {
        setSelectedAppointmentId(null);
        handlePickupDateChange(e.target.value);
      }

      setShowWholesaleDatePicker(false); // Hide wholesale picker if regular date selected
    } else {
      // Clear pickup room and hide wholesale picker
      setShowWholesaleDatePicker(false);
      setSelectedAppointmentId(null);
      setTaskData(prev => ({
        ...prev,
        allTasks: prev.allTasks.filter(task => {
          const roomNum = parseInt(task.roomNo);
          return !(roomNum === 7001 || roomNum === 21001 || roomNum === 32001);
        })
      }));
    }
  }}
  className={(() => {
    const pickupRoom = taskData.allTasks.find(task => {
      const roomNum = parseInt(task.roomNo);
      return roomNum === 7001 || roomNum === 21001 || roomNum === 32001;
    });
    return !pickupRoom?.taskDueDate ? 'form-control required-field-error' : 'form-control';
  })()}
  required={true}
>
  <option value="">Select an appointment...</option>
  
  <option value="CREATE_NEW" style={{ 
    fontWeight: 'bold', 
    color: '#4027b0',
    backgroundColor: '#f8f9fa'
  }}>
    + Create an appointment
  </option>

  {/* Add "Wholesale" option for ChenW */}
  {(currentUser === 'ChenW' || currentUser === 'DidiP'  || currentUser === 'Zehavi' 
  || currentUser === 'Noga' || currentUser === 'Ayala') && (
    <option value="WHOLESALE" style={{
      fontWeight: 'bold',
      color: '#d32f2f',
      backgroundColor: '#fffbe6'
    }}>
      + Wholesale
    </option>
  )}

  {Array.isArray(customerAppointments) &&
    customerAppointments
      .filter(appointment => {
        if (!appointment.EventStart) return false;

        // Filter out cancelled appointments
        if (appointment.AppointmentStatus &&
            appointment.AppointmentStatus.toLowerCase() === 'cancelled') {
          return false;
        }

        const appointmentDate = parseEventDate(appointment.EventStart);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return appointmentDate && appointmentDate >= today;
      })
      .map((appointment, index) => {
        const eventDate = parseEventDate(appointment.EventStart);
        const formattedDate = eventDate ? eventDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : 'Invalid Date';

        // Build display text with appointment type, status, and meeting person
        const appointmentType = appointment.title || appointment.Title || 'Appointment';
        const status = appointment.AppointmentStatus || '';
        const meetingWith = appointment.MeetingWith || '';

        const displayText = `${formattedDate} - ${appointmentType}${status ? ` [${status}]` : ''}${meetingWith ? ` with ${meetingWith}` : ''}`;

        return (
          <option key={index} value={appointment.EventStart}>
            {displayText}
          </option>
        );
      })
  }
</select>
        {/* Add this right after the closing </select> tag */}
        {showWholesaleDatePicker && (
          <div style={{ 
            marginTop: '15px', 
            padding: '15px', 
            backgroundColor: 'var(--dark-elevated)', 
            border: '1px solid var(--dark-border)', 
            borderRadius: '6px' 
          }}>
            <h4 style={{ 
              margin: '0 0 10px 0', 
              color: '#d32f2f', 
              fontSize: '16px' 
            }}>
              Wholesale Date Selection
            </h4>
            <EnhancedDatePicker
              id="wholesale-pickup-date"
              label="Select Wholesale Pickup Date:"
              value={(() => {
                const pickupRoom = taskData.allTasks.find(task => {
                  const roomNum = parseInt(task.roomNo);
                  return roomNum === 7001 || roomNum === 21001 || roomNum === 32001;
                });
                return pickupRoom?.taskDueDate || '';
              })()}
              onChange={(e) => {
                if (e.target.value) {
                  handlePickupDateChange(e.target.value);
                  setShowWholesaleDatePicker(false);

                }
              }}
              required={true}
            />
            <div style={{ 
              marginTop: '10px', 
              display: 'flex', 
              gap: '10px' 
            }}>
              <button 
                onClick={() => setShowWholesaleDatePicker(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--red-accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {/* QC Worker Dropdown */}
      <div style={{ flex: 1 }}>
        <label style={{
          color: 'var(--text-primary)',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: '6px',
          display: 'block'
        }}>
          Select QC Worker:
        </label>
        <select 
          value={selectedQcWorker}
          onChange={(e) => handleQcWorkerChange(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 14px',
            fontSize: '14px',
            backgroundColor: 'var(--dark-elevated)',
            color: 'var(--text-primary)',
            border: '1px solid var(--dark-border)',
            borderRadius: '6px',
            transition: 'all 0.3s ease',
            outline: 'none',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = 'var(--purple-accent)';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = 'var(--dark-border)';
          }}
        >
          {qcWorkers.map((worker, index) => (
            <option key={index} value={worker.userName || worker.UserName}>
              {worker.UserName || worker.userName}
            </option>
          ))}
        </select>
      </div>
    </div>
    
    {/* Status Messages */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      {/* Wash Room Status */}
      {customerInfo?.orderType === 'Wash & Set' && (() => {
        const washRoom = taskData.allTasks.find(task => {
          const washRoomNumbers = ['5001', '23001', '34001'];
          return washRoomNumbers.includes(task.roomNo);
        });
        const styleRoom = taskData.allTasks.find(task => {
          const styleRoomNumbers = ['5001', '23001', '34001'];
          return styleRoomNumbers.includes(task.roomNo);
        });
        
        if (washRoom && styleRoom) {
          return (
            <span className="validation-success">
              ✅ Wash & Set rooms auto-added: {washRoom.location} - Wash (Room {washRoom.roomNo}), Style (Room {styleRoom.roomNo})
            </span>
          );
        } else if (washRoom || styleRoom) {
          return (
            <span className="validation-message">
              ⚠️ Only one Wash & Set room found - both wash and style rooms should be auto-added
            </span>
          );
        }
        return (
          <span className="validation-message">
            ⚠️ Wash and Style rooms should be auto-added for Wash & Set orders
          </span>
        );
      })()}
      
      {/* Pickup Date Status */}
      {(() => {
        const pickupRoom = taskData.allTasks.find(task => {
          const roomNum = parseInt(task.roomNo);
          return roomNum === 7001 || roomNum === 21001 || roomNum === 32001;
        });
        if (!pickupRoom?.taskDueDate) {
          return <span className="validation-message">⚠️ Customer pickup date is required to proceed</span>;
        }
        return (
          <span className="validation-success">
            ✅ Pickup scheduled for {pickupRoom.location} (Room {pickupRoom.roomNo}) on {formatDate(pickupRoom.taskDueDate)}
          </span>
        );
      })()}
      
      {/* QC Worker Status */}
      {(() => {
        const qcRoom = taskData.allTasks.find(task => {
          const qcRoomNumbers = ['10001', '22001', '33001'];
          return qcRoomNumbers.includes(task.roomNo);
        });
        if (!qcRoom) {
            if (customerInfo?.orderType === 'Wash & Set') {
            return null;
            }
            return <span className="validation-message">⚠️ QC worker selection recommended</span>;
        }
        return (
          <span className="validation-success">
            ✅ QC assigned to {qcRoom.assignedWorker} in {qcRoom.location} (Room {qcRoom.roomNo})
          </span>
        );
      })()}
    </div>
  </div>
</div>

      {/* Wig Room Routing Header */}
      <div className="routing-header">
        <h3>Wig Room Routing</h3>
        
        <button 
          className="routing-toggle-btn"
          onClick={toggleRoutingSettings}
        >
          {taskData.showRouting ? 'Close Routing' : 'Add/Edit Routing'}
        </button>
      </div>
           {/* AI draft rooms indicator — verify stylist instructions landed correctly */}
      {draftRooms && draftRooms.length > 0 && (
        <DraftRoomsIndicator rooms={draftRooms} />
      )}

      {/* Routing Settings Grid */}
      {taskData.showRoutingSettings && (
        <div className="routing-settings">
          <table className="routing-table">
            <thead>
              <tr>
                <th>Room</th>
                <th>Task</th>
                <th>Worker</th>
                <th>Task DueDate</th>
                <th>Notes</th>
                <th>QC Notes</th>
                <th>Scan In</th>
                <th>Scan Out</th>
                <th>Hrs</th>
                <th>Transfer By</th>
                <th>Transfer Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {taskData.allTasks.map(task => (
                <tr key={task.id}>
                  <td>{task.roomNo}</td>
                  <td>{task.roomTask}</td>
                  <td>{task.assignedWorker}</td>
                  <td>{formatDate(task.taskDueDate)}</td>
                  <td>{task.taskNotes}</td>
                  <td>{task.qcNotes || ''}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>Pending</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
    
    {/* Add/Edit Routing Section */}
    {taskData.showRouting && (
      <div className="routing-editor">
        {(() => {
          const userLocation = getUserLocation();
          const otherLocations = getOtherLocations(userLocation);
          
          return (
            <>
              {/* User's Primary Location - Always shown first */}
              <div className="primary-location-section">
                {/* <div style={{
                  backgroundColor: 'var(--gradient-secondary)',
                  color: 'var(--text-primary)',
                  padding: '10px 15px',
                  borderRadius: '6px',
                  marginBottom: '15px',
                  border: '1px solid var(--purple-accent)',
                  fontSize: '14px'
                }}>
                  <strong>🏢 Your Primary Location:</strong> {userLocation}
                </div> */}
                {renderLocationSection(userLocation)}
              </div>
              
              {/* Toggle Button for Other Locations */}
              <div className="location-toggle-section">
                <button 
                  onClick={() => setShowAllLocations(!showAllLocations)}
                  className="toggle-locations-btn"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: showAllLocations ? 'var(--red-accent)' : 'var(--purple-accent)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    margin: '15px 0'
                  }}
                  onMouseEnter={(e) => {
                    if (showAllLocations) {
                      e.target.style.backgroundColor = '#d32f2f';
                    } else {
                      e.target.style.backgroundColor = '#5a4fcf';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (showAllLocations) {
                      e.target.style.backgroundColor = 'var(--red-accent)';
                    } else {
                      e.target.style.backgroundColor = 'var(--purple-accent)';
                    }
                  }}
                >
                  {showAllLocations ? 'Hide Other Locations' : `Show Other Locations (${otherLocations.join(', ')})`}
                </button>
              </div>
              
              {/* Other Locations - Only shown when toggled */}
              {showAllLocations && (
                <div className="other-locations-section">
                  <div style={{
                    backgroundColor: 'var(--dark-elevated)',
                    color: 'var(--text-secondary)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    border: '1px solid var(--dark-border)',
                    fontSize: '12px',
                    fontStyle: 'italic'
                  }}>
                    📍 Additional Locations (for cross-location routing)
                  </div>
                  
                  {otherLocations.map(location => (
                    <div key={location} className="other-location-wrapper">
                      {renderLocationSection(location)}
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}
        
        {/* All Tasks Grid - remains the same */}
        <div className="all-tasks-section">
          <table className="all-tasks-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Room No</th>
                <th>Room Task</th>
                <th>Assigned Worker</th>
                <th>Task Due Date</th>
                <th>Task Notes</th>
                <th>QC Notes</th>              
              </tr>
            </thead>
            <tbody>
              {taskData.allTasks.map(task => (
                <tr key={task.id}>
                  <td data-label="">
                    <button onClick={() => deleteTask(task.id)}>Delete</button>
                  </td>
                  <td data-label="Room No">{task.roomNo}</td>
                  <td data-label="Task">
                    <input
                      type="text"
                      value={task.roomTask}
                      onChange={(e) => updateTaskField(task.id, 'roomTask', e.target.value)}
                      className="edit-text"
                    />
                  </td>
                  <td data-label="Worker">
                    <input
                      type="text"
                      value={task.assignedWorker}
                      onChange={(e) => updateTaskField(task.id, 'assignedWorker', e.target.value)}
                      className="edit-text"
                    />
                  </td>
                  <td data-label="Due Date">
                    <input
                      type="date"
                      value={task.taskDueDate}
                      onChange={(e) => updateTaskField(task.id, 'taskDueDate', e.target.value)}
                      className="edit-date"
                    />
                  </td>
                  <td data-label="Notes">
                    <input
                      type="text"
                      value={task.taskNotes}
                      onChange={(e) => updateTaskField(task.id, 'taskNotes', e.target.value)}
                      className="edit-text"
                    />
                  </td>
                  <td data-label="QC Notes">
                    <input
                      type="text"
                      value={task.qcNotes || ''}
                      onChange={(e) => updateTaskField(task.id, 'qcNotes', e.target.value)}
                      className="edit-text"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
      
      <div className="button-group">
        <button
          type="button"
          className="back-btn"
          onClick={handlePreviousClick}
        >
          Back to Wig Details
        </button>
        <button
          type="button"
          className="submit-btn"
          onClick={handleNextClick}
        >
          Next - Payment
        </button>
      </div>

      <CreateAppointmentModal
  isOpen={showModal}
  onClose={() => setShowModal(false)}
  customerInfo={customerInfo}
  currentUser={currentUser}
  prefillData={{ Title: 'Customer Pickup' }}
  woeMode={true}
  onAppointmentCreated={async (newAppointment) => {
    // Auto-select the newly created appointment so its ID flows through to order submission
    const newId = newAppointment?.ID || newAppointment?.id;
    if (newId) {
      setSelectedAppointmentId(newId);
      console.log('📅 Auto-selected newly created appointment:', newId);
    }

    // Refresh the customer appointments after creating new one
    if (customerInfo?.customerNo) {
      try {
        console.log('Refreshing appointments after creation...');
        const response = await fetch(buildUrl(`/api/appointments/customer/${customerInfo.customerNo}`));
        if (response.ok) {
          const data = await response.json();
          setCustomerAppointments(data.appointments || []);
          console.log('Appointments refreshed:', data.appointments);
        }
      } catch (error) {
        console.error('Error refreshing appointments:', error);
        // Fallback: just add the new appointment to existing list
        setCustomerAppointments(prev => [...(prev || []), newAppointment.appointment]);
      }
    }
  }}
/>
    </div>
  );
};

// ── Stylist draft rooms summary indicator ─────────────────────────────────────
function DraftRoomsIndicator({ rooms }) {
  const [collapsed, setCollapsed] = React.useState(false);

  return (
    <div style={{
      backgroundColor: '#fffbeb',
      border: '1px solid #f59e0b',
      borderLeft: '4px solid #f59e0b',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '16px',
      fontSize: '13px',
      color: '#78350f',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: collapsed ? 0 : 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '16px' }}>🗣</span>
          <strong>Stylist's room instructions — verify these notes are filled in correctly below</strong>
        </div>
        <button
          onClick={() => setCollapsed(c => !c)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: 13, padding: '2px 6px' }}
        >
          {collapsed ? 'Show ▼' : 'Hide ▲'}
        </button>
      </div>

      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rooms.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{
                background: '#fde68a', color: '#78350f',
                borderRadius: 20, padding: '2px 10px',
                fontSize: 12, fontWeight: 700,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {r.roomName}
              </span>
              <span style={{ color: '#92400e', lineHeight: 1.5 }}>
                {r.taskNotes || <em style={{ opacity: 0.6 }}>no notes</em>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WOEWigRooms;